import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { getElementFont, getFittingText } from "../../helpers/text-helper";
import { BarTask } from "../../types/bar-task";
import { GanttContentMoveAction } from "../../types/gantt-asset-actions";
import style from "./asset-item.module.css";
import { Bar } from "./bar/bar";
import { BarSmall } from "./bar/bar-small";

export type AssetItemProps = {
  asset: BarTask;
  arrowIndent: number;
  assetHeight: number;
  isSelected: boolean;
  isHighlighted?: boolean;
  svgWidth: number;
  processLinesRef: React.MutableRefObject<SVGElement | null>;
  onEventStart: (
    action: GanttContentMoveAction,
    selectedAsset: BarTask,
    event?: React.MouseEvent,
  ) => any;
};

export const AssetItem: React.FC<AssetItemProps> = (props) => {
  const {
    asset,
    arrowIndent,
    assetHeight,
    isSelected,
    isHighlighted,
    onEventStart,
    svgWidth,
  } = {
    ...props,
  };
  const labelRef = useRef<SVGTextElement>(null);
  const [assetItem, setAssetItem] = useState<JSX.Element>(<div />);
  const [labelFont, setLabelFont] = useState("");

  let begin = asset.x1 < 0 ? 0 : asset.x1;
  let end = asset.x2 > svgWidth ? svgWidth : asset.x2;

  let hasMinWidth = end - begin > 50;
  const visibleCenterX = (begin + end) / 2;

  // Calculate if there's enough width to show time texts (HH:mm format)
  // Approximate width needed for "HH:mm" text is about 35-40 pixels
  const timeTextMinWidth = 40; // Width needed for both textLeftTop and textRightBottom
  const hasWidthForTimeTexts = asset.x2 - asset.x1 > timeTextMinWidth;

  useEffect(() => {
    switch (asset.type) {
      case "taskLine":
        setAssetItem(<BarSmall {...props} />);
        break;
      default:
        setAssetItem(<Bar {...props} isHighlighted={isHighlighted} />);
        break;
    }
  }, [asset, isSelected, isHighlighted]);

  useLayoutEffect(() => {
    if (labelRef.current) {
      setLabelFont(getElementFont(labelRef.current));
    }
  }, []);

  const labelPadding = 4;
  const labelCandidates =
    asset.name.toLowerCase() === "unlinked"
      ? [asset.name, "U"]
      : [asset.name, asset.name.slice(-2), asset.name.slice(-1)];
  const label = labelFont
    ? getFittingText(labelCandidates, labelFont, end - begin - labelPadding)
    : asset.name;

  // const getX = () => {
  // 	if (asset.x1 < 0) {
  // 		if (asset.x2 < svgWidth) {
  // 			return asset.x2 / 2;
  // 		} else {
  // 			return svgWidth / 2;
  // 		}
  // 	}
  // 	if (asset.x2 > svgWidth) {
  // 		if (asset.x1 > 0) {
  // 			return asset.x1 + (svgWidth - asset.x1) / 2;
  // 		} else {
  // 			return svgWidth / 2;
  // 		}
  // 	}
  // 	const width = asset.x2 - asset.x1;
  // 	const hasChild = asset.barChildren.length > 0;

  // 	if (isTextInside) {
  // 		return asset.x1 + width * 0.5;
  // 	}
  // 	return asset.x1 + width + arrowIndent * +hasChild + arrowIndent * 0.2;
  // };
  const subLabelYPadding: number = 0.2;
  const subLabelXPadding: number = 0.05;

  const getSubX = (left: boolean, isTop: boolean) => {
    let begin = asset.x1;
    let end = asset.x2;

    if (asset.x1 < 0) {
      begin = 0;
      if (asset.x2 < svgWidth) {
        end = asset.x2;
      } else {
        end = svgWidth;
      }
    }

    if (asset.x2 > svgWidth) {
      end = svgWidth;
      if (asset.x1 > 0) {
        begin = asset.x1;
      } else {
        begin = 0;
      }
    }

    const margin = 10;
    return left
      ? begin + (end - begin) * subLabelXPadding - (isTop ? 0 : margin)
      : begin + (end - begin) * (1 - subLabelXPadding) + (isTop ? 0 : margin);
  };

  const getYPosition = (isTop: boolean, isBottom: boolean) => {
    const margin = 10;
    if (isTop) {
      return asset.y - margin;
    }
    if (isBottom) {
      return asset.y + assetHeight + margin;
    }
    return asset.y + assetHeight * 0.5;
  };

  return (
    <g
      onMouseEnter={(e) => {
        onEventStart("mouseenter", asset, e);
      }}
      onMouseLeave={(e) => {
        onEventStart("mouseleave", asset, e);
      }}
      onDoubleClick={(e) => {
        onEventStart("dblclick", asset, e);
      }}
      onFocus={() => {
        onEventStart("select", asset);
      }}
      onContextMenu={(e) => {
        onEventStart("contextMenu", asset, e);
      }}
    >
      {assetItem}

      <text
        x={visibleCenterX}
        y={asset.y + assetHeight * 0.5}
        textAnchor="middle"
        dominantBaseline="middle"
        className={style.barLabel}
        ref={labelRef}
      >
        {label}
      </text>

      {hasMinWidth ? (
        <>
          {hasWidthForTimeTexts && (
            <text
              x={asset.x1}
              y={getYPosition(true, false)}
              className={style.barSubLabel}
            >
              {asset.textLeftTop}
            </text>
          )}
          <text
            x={getSubX(false, true)}
            y={getYPosition(true, false) + 20}
            className={[style.barSubLabel, style.barSubLabelRight].join(" ")}
          >
            {asset.textRightTop}
          </text>
          <text
            x={visibleCenterX}
            y={asset.y + assetHeight / 2 + 15}
            className={[style.barSubLabel, style.gradeLabel].join(" ")}
            textAnchor="middle"
            dominantBaseline="middle"
          >
            {asset.textLeftBottom}
          </text>
          {hasWidthForTimeTexts && (
            <text
              x={asset.x2}
              y={getYPosition(false, true)}
              className={[style.barSubLabel, style.barSubLabelRight].join(" ")}
            >
              {asset.textRightBottom}
            </text>
          )}
        </>
      ) : (
        <>
          {hasWidthForTimeTexts && (
            <text
              x={asset.x1}
              y={getYPosition(true, false)}
              className={style.barSubLabel}
            >
              {asset.textLeftTop}
            </text>
          )}
          <text
            x={getSubX(false, true)}
            y={getYPosition(true, false) + 20}
            className={[style.barSubLabel, style.barSubLabelRight].join(" ")}
          >
            {asset.textRightTop}
          </text>
          {hasWidthForTimeTexts && (
            <text
              x={asset.x2}
              y={getYPosition(false, true)}
              className={[style.barSubLabel, style.barSubLabelRight].join(" ")}
            >
              {asset.textRightBottom}
            </text>
          )}
        </>
      )}
    </g>
  );
};
